import { Avatar, Box, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@material-ui/core";
import { Assignment, Event, Notes } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import type { REST_schema } from "../../../../@types/sharedSchema";

type Props = {
  activity: REST_schema.ListingOrgActivity;
  /** Cap rendered rows (the popup passes a small number; the dialog shows all). */
  maxItems?: number;
};

const TYPE_ICONS: Record<REST_schema.ListingOrgActivityItemType, JSX.Element> = {
  meeting: <Event sx={{ fontSize: 14, color: "primary.main" }} />,
  task: <Assignment sx={{ fontSize: 14, color: "info.main" }} />,
  note: <Notes sx={{ fontSize: 14, color: "warning.main" }} />
};

/**
 * Short newest-first feed of org-mates' activity on one listing (shared notes,
 * meetings, tasks). Rendered inside the indicator popup and in the listing
 * details dialog. Presentational only — data comes from useListingOrgActivity.
 */
export function ListingOrgActivityTimeline({ activity, maxItems }: Props) {
  const { t } = useTranslation();
  const items = maxItems ? activity.items.slice(0, maxItems) : activity.items;
  const hiddenCount = activity.totalCount - items.length;

  return (
    <>
      <List dense disablePadding>
        {items.map((item, index) => {
          const date = item.actionTime ? new Date(item.actionTime) : null;
          const dateLabel = date && !isNaN(date.getTime()) ? moment(date).locale("he").format("LL") : "";
          return (
            <ListItem key={`${item.actionUser.userId}-${item.actionTime}-${index}`} disableGutters alignItems="flex-start">
              <ListItemAvatar sx={{ minWidth: 34 }}>
                <Avatar src={item.actionUser.userAvatar || undefined} alt={item.actionUser.userName} sx={{ width: 26, height: 26, fontSize: 12 }}>
                  {item.actionUser.userName?.charAt(0)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    {TYPE_ICONS[item.actionType]}
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                      }}
                    >
                      {item.actionDescription}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {item.actionUser.userName}
                    {` · ${t(`listingsBoard.orgActivity.type.${item.actionType}`)}`}
                    {dateLabel && ` · ${dateLabel}`}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
      {hiddenCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          {t("listingsBoard.orgActivity.more", { count: hiddenCount })}
        </Typography>
      )}
    </>
  );
}
